import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const DepositsTab = ({ bankAccounts, onRefresh }) => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    bankAccountId: '',
    status: ''
  });
  const [showNewDeposit, setShowNewDeposit] = useState(false);

  useEffect(() => {
    loadDeposits();
  }, [filters]);

  const loadDeposits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.bankAccountId) params.append('bankAccountId', filters.bankAccountId);
      
      const data = await api.request(`/accounting/deposits?${params.toString()}`);
      setDeposits(data);
    } catch (err) {
      console.error('Failed to load deposits:', err);
      alert('Failed to load deposits: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  // Calculate summary stats
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonthDeposits = deposits.filter(d => new Date(d.depositDate) >= startOfMonth);
  const totalThisMonth = thisMonthDeposits.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
  const totalAllTime = deposits.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-500">Loading deposits...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Deposits</h2>
          <p className="text-sm text-neutral-500 mt-1">
            Track all bank deposits
          </p>
        </div>
        <button
          onClick={() => setShowNewDeposit(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          New Deposit
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Total Deposits (This Month)</div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(totalThisMonth)}
          </div>
          <div className="text-xs text-neutral-500 mt-1">{thisMonthDeposits.length} deposits</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Total Deposits (All Time)</div>
          <div className="text-2xl font-bold">{formatCurrency(totalAllTime)}</div>
          <div className="text-xs text-neutral-500 mt-1">{deposits.length} deposits</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Average Deposit</div>
          <div className="text-2xl font-bold">
            {formatCurrency(deposits.length > 0 ? totalAllTime / deposits.length : 0)}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => setFilters({...filters, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => setFilters({...filters, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Bank Account</label>
            <select
              value={filters.bankAccountId}
              onChange={e => setFilters({...filters, bankAccountId: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            >
              <option value="">All Accounts</option>
              {bankAccounts?.map(account => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ startDate: '', endDate: '', bankAccountId: '', status: '' })}
              className="w-full px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-left font-medium">Bank Account</th>
              <th className="px-4 py-3 text-right font-medium">Amount</th>
              <th className="px-4 py-3 text-left font-medium">Reference #</th>
              <th className="px-4 py-3 text-left font-medium">Payments</th>
              <th className="px-4 py-3 text-left font-medium">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {deposits.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-neutral-500">
                  No deposits found
                </td>
              </tr>
            ) : (
              deposits.map(deposit => (
                <tr key={deposit.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3">{formatDate(deposit.depositDate)}</td>
                  <td className="px-4 py-3 font-medium">{deposit.bankAccountName || '-'}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-green-600">
                    {formatCurrency(deposit.amount)}
                  </td>
                  <td className="px-4 py-3">{deposit.referenceNumber || '-'}</td>
                  <td className="px-4 py-3">{deposit.paymentCount || 0} payment{deposit.paymentCount !== 1 ? 's' : ''}</td>
                  <td className="px-4 py-3 text-neutral-500">{deposit.notes || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* New Deposit Modal - Simplified for now */}
      {showNewDeposit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">New Deposit</h3>
            <p className="text-neutral-600 mb-4">
              To create a deposit, go to the "Pending Payments" tab and select payments to deposit.
            </p>
            <button
              onClick={() => setShowNewDeposit(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositsTab;
